/**
 * CR-8227: set up a new customer's organisation from Platform Config, as the
 * approved mockup lays it out — choose the structure, name its levels, then
 * enter the group and a row per division and site (details in the panel under
 * the table) and create them in one go. Shown only while the instance has no
 * customer structure at all; after that the Helix team works in the tree.
 */
import { useEffect, useState } from 'react';
import type { OrganisationStructure } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useConfirm } from '../../../contexts/ConfirmDialogContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { pluralize } from '../../orgchart/types';
import type { NodeConfigForm } from '../../orgchart/useNodeConfigForm';
import { OrganisationLevelNames } from './OrganisationLevelNames';
import { OrganisationSetupRows } from './OrganisationSetupRows';
import { OrganisationRowDetails } from './OrganisationRowDetails';
import {
  LEVEL_COLORS,
  SHAPES,
  SetupRunError,
  findGroupNameClashes,
  hasCreatedAnything,
  initialDraft,
  levelsFor,
  newDivisionRow,
  newSiteRow,
  reshape,
  runSetup,
  validateDraft,
  type LevelNames,
  type OrganisationShape,
  type SetupDraft,
  type SetupRow,
} from './organisationSetup';

interface OrganisationSetupFormProps {
  /** The level names in force today, the starting point for step b. */
  currentLabels: LevelNames;
  /** Called once the structure and level names are saved (before any create). */
  onSettingsSaved: (structure: OrganisationStructure, labels: LevelNames) => void;
  /** Called when every record is created, or when leaving a part-created setup. */
  onCreated: () => void;
}

/** The structure the form opens on, as in the mockup. */
const DEFAULT_SHAPE: OrganisationShape = 'group_sites';

/** The shape drawn with the level names being typed. */
function diagram(shape: OrganisationShape, labels: LevelNames): string {
  if (shape === 'single_site') return labels.site;
  if (shape === 'group_sites') return `${labels.group}\n  ${labels.site}\n  ${labels.site}`;
  return `${labels.group}\n  ${labels.division}\n    ${labels.site}\n  ${labels.division}\n    ${labels.site}`;
}

const SETUP_TITLES: Record<OrganisationShape, (labels: LevelNames) => string> = {
  single_site: (l) => `the ${l.site.toLowerCase()}`,
  group_sites: (l) => `the ${l.group.toLowerCase()} and its ${pluralize(l.site).toLowerCase()}`,
  group_divisions_sites: (l) =>
    `the ${l.group.toLowerCase()}, its ${pluralize(l.division).toLowerCase()} and their ${pluralize(l.site).toLowerCase()}`,
};

const firstSiteKey = (draft: SetupDraft) => draft.rows.find((row) => row.kind === 'site')?.key ?? null;

/**
 * @param currentLabels - the instance's level names today
 * @param onSettingsSaved - lets the page pick up the saved structure and names
 * @param onCreated - reloads the section once the organisation exists
 */
export function OrganisationSetupForm({ currentLabels, onSettingsSaved, onCreated }: OrganisationSetupFormProps) {
  const confirm = useConfirm();
  const [draft, setDraft] = useState<SetupDraft>(() => initialDraft(DEFAULT_SHAPE, currentLabels));
  const [selectedKey, setSelectedKey] = useState<string | null>(() => firstSiteKey(draft));
  const [problems, setProblems] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [labelsEdited, setLabelsEdited] = useState(false);

  // The page's settings can arrive after this form first renders. Until a level
  // name is edited here, follow the names the instance holds.
  const { group: currentGroup, division: currentDivision, site: currentSite } = currentLabels;
  useEffect(() => {
    if (labelsEdited) return;
    setDraft((prev) => ({ ...prev, labels: { group: currentGroup, division: currentDivision, site: currentSite } }));
  }, [currentGroup, currentDivision, currentSite, labelsEdited]);

  const started = hasCreatedAnything(draft);
  const { labels } = draft;
  const levels = levelsFor(draft.shape);
  const divisions = draft.rows.filter((row) => row.kind === 'division');
  const sites = draft.rows.filter((row) => row.kind === 'site');
  const selectedRow = draft.rows.find((row) => row.key === selectedKey) ?? null;

  const pickShape = async (shape: OrganisationShape) => {
    if (started || running || draft.shape === shape) return;
    const next = reshape(draft, shape);
    if (next.dropped > 0) {
      const ok = await confirm({
        title: 'Change the structure?',
        description: `${SHAPES.find((s) => s.id === shape)?.title} has no place for ${next.dropped} of the rows `
          + 'you filled in, so they will be removed.',
        confirmLabel: 'Change structure',
      });
      if (!ok) return;
    }
    setProblems([]);
    setDraft(next.draft);
    if (!next.draft.rows.some((row) => row.key === selectedKey)) setSelectedKey(firstSiteKey(next.draft));
  };

  // Applied to the latest draft, never to a row captured at render, so quick
  // successive edits (typing, autofill) cannot undo each other.
  const updateRow = (key: string, change: (row: SetupRow) => SetupRow) =>
    setDraft((prev) => ({ ...prev, rows: prev.rows.map((row) => (row.key === key ? change(row) : row)) }));
  const setRowField = (key: string, field: keyof NodeConfigForm, value: string) =>
    updateRow(key, (row) => ({ ...row, form: { ...row.form, [field]: value } }));
  const setRowParent = (key: string, parentKey: string) => updateRow(key, (row) => ({ ...row, parentKey }));
  const setGroupField = (field: keyof NodeConfigForm, value: string) =>
    setDraft((prev) => ({ ...prev, group: { ...prev.group, [field]: value } }));

  const addSite = () => {
    const row = newSiteRow(divisions[divisions.length - 1]?.key ?? null);
    setDraft((prev) => ({ ...prev, rows: [...prev.rows, row] }));
    setSelectedKey(row.key);
  };
  const addDivision = () => {
    const division = newDivisionRow();
    setDraft((prev) => ({ ...prev, rows: [...prev.rows, division, newSiteRow(division.key)] }));
    setSelectedKey(division.key);
  };
  const removeRow = (key: string) => {
    const next = { ...draft, rows: draft.rows.filter((row) => row.key !== key) };
    setDraft(next);
    setSelectedKey(firstSiteKey(next));
  };
  const removeBlockedReason = (row: SetupRow): string | null => {
    if (row.kind === 'site') {
      return sites.length > 1 ? null : `There must be at least one ${labels.site.toLowerCase()}.`;
    }
    if (sites.some((site) => site.parentKey === row.key)) {
      return `Move or remove its ${pluralize(labels.site).toLowerCase()} first.`;
    }
    return divisions.length > 1 ? null : `There must be at least one ${labels.division.toLowerCase()}.`;
  };

  const cancel = async () => {
    const ok = await confirm({
      title: 'Discard this setup?',
      description: 'Nothing has been created yet. The level names and rows you entered will be cleared.',
      confirmLabel: 'Discard',
      variant: 'destructive',
    });
    if (!ok) return;
    const fresh = initialDraft(DEFAULT_SHAPE, currentLabels);
    setDraft(fresh);
    setSelectedKey(firstSiteKey(fresh));
    setLabelsEdited(false);
    setProblems([]);
  };

  const create = async () => {
    const issues = validateDraft(draft);
    setProblems(issues);
    if (issues.length) {
      toast.error(issues[0]);
      return;
    }
    setRunning(true);
    try {
      const clashes = await findGroupNameClashes(draft);
      if (clashes.length) {
        setProblems(clashes);
        toast.error(clashes[0]);
        return;
      }
      const done = await runSetup(draft);
      setDraft(done);
      onSettingsSaved(done.shape, done.labels);
      toast.success('Organisation created');
      onCreated();
    } catch (err: unknown) {
      if (err instanceof SetupRunError) {
        setDraft(err.draft);
        if (err.draft.settingsSaved) onSettingsSaved(err.draft.shape, err.draft.labels);
        setProblems([err.message, 'Fix the problem and choose Finish setup; what was already created is kept.']);
        toast.error(err.message);
      } else {
        const message = getApiErrorMessage(err, 'Could not check the names. Please try again.');
        setProblems([message]);
        toast.error(message);
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-7">
      <section className="space-y-3">
        <h3 className="font-semibold">a. Choose the structure</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {SHAPES.map((shape) => {
            const selected = draft.shape === shape.id;
            return (
              <button
                key={shape.id}
                type="button"
                aria-pressed={selected}
                disabled={(started || running) && !selected}
                onClick={() => pickShape(shape.id)}
                className={cn(
                  'flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                  selected ? 'border-2 border-primary bg-primary/5' : 'hover:bg-muted/50',
                )}
              >
                <span className="flex items-center gap-2 font-semibold">
                  <span
                    className={cn('h-3.5 w-3.5 shrink-0 rounded-full', selected ? 'border-[4px] border-primary' : 'border-2 border-muted-foreground')}
                    aria-hidden="true"
                  />
                  {shape.title}
                </span>
                <span className="text-sm text-muted-foreground">{shape.description}</span>
                <span className="whitespace-pre rounded-md bg-muted px-3 py-2 font-mono text-xs">{diagram(shape.id, labels)}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold">b. Name each level (what the customer sees in their own screens)</h3>
        <OrganisationLevelNames
          structure={draft.shape}
          labels={labels}
          disabled={running}
          onChange={(next) => {
            setLabelsEdited(true);
            setDraft((prev) => ({ ...prev, labels: next, settingsSaved: false }));
          }}
        />
        <p className="text-xs text-muted-foreground">
          Only the levels the chosen structure uses are shown. One value for the whole instance; customer saves no
          longer keep their own copy.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold">c. Set up {SETUP_TITLES[draft.shape](labels)}</h3>
        {levels.group && (
          <div className="space-y-3 rounded-md border p-4">
            <div className="flex items-center gap-2">
              <Badge className={cn('text-xs text-white', LEVEL_COLORS.group.fill)}>{labels.group}</Badge>
              <span className="font-semibold">{labels.group} details</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {([
                ['name', 'Name *', ''],
                ['description', 'Description', 'Brief description'],
                ['country', 'Country', ''],
                ['website', 'Website', 'https://'],
              ] as Array<[keyof NodeConfigForm, string, string]>).map(([field, title, placeholder]) => (
                <div key={field} className="space-y-1">
                  <Label htmlFor={`setup-group-${field}`} className="font-normal">{title}</Label>
                  <Input
                    id={`setup-group-${field}`}
                    value={String(draft.group[field])}
                    disabled={running || Boolean(draft.groupId)}
                    onChange={(e) => setGroupField(field, e.target.value)}
                    placeholder={placeholder}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <OrganisationSetupRows
          draft={draft}
          disabled={running}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          onAddDivision={addDivision}
          onAddSite={addSite}
        />
        {selectedRow && (
          <OrganisationRowDetails
            // A fresh panel per row, so nothing typed while switching rows can
            // land on the row being left.
            key={selectedRow.key}
            row={selectedRow}
            shape={draft.shape}
            labels={labels}
            divisions={divisions}
            disabled={running}
            removeBlockedReason={removeBlockedReason(selectedRow)}
            onFieldChange={setRowField}
            onParentChange={setRowParent}
            onRemove={removeRow}
          />
        )}
      </section>

      {problems.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {problems.map((problem) => <li key={problem}>{problem}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap justify-end gap-3">
        {started ? (
          <Button variant="outline" disabled={running} onClick={onCreated}>
            Stop here and manage it in the tree
          </Button>
        ) : (
          <Button variant="outline" disabled={running} onClick={cancel}>Cancel</Button>
        )}
        <Button disabled={running} onClick={create}>
          {running && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {started ? 'Finish setup' : 'Create organisation'}
        </Button>
      </div>
    </div>
  );
}
